/**
 * Загрузчик медиа с контролем параллельности, retry и LRU-кешем.
 * Используется хуками useImageProxy / useMediaProxy.
 */

const MAX_CONCURRENT = 3;
const MAX_RETRIES = 3;
const RETRY_DELAYS = [1000, 2000, 4000];
const MAX_CACHE_SIZE = 120;

type LoadStatus = 'loading' | 'done' | 'error';

interface CacheEntry {
  blobUrl: string;
  isBlob: boolean;
  accessedAt: number;
}

interface QueueItem {
  url: string;
  apiKey?: string;
  resolve: (result: { blobUrl: string; isBlob: boolean }) => void;
  reject: (error: Error) => void;
}

class MediaLoader {
  private cache = new Map<string, CacheEntry>();
  private statusMap = new Map<string, LoadStatus>();
  private listeners = new Map<string, Set<() => void>>();
  private queue: QueueItem[] = [];
  private activeCount = 0;

  getStatus(url: string): LoadStatus | undefined {
    return this.statusMap.get(url);
  }

  getCachedUrl(url: string): string | null {
    const entry = this.cache.get(url);
    if (entry) {
      entry.accessedAt = Date.now();
      return entry.blobUrl;
    }
    return null;
  }

  subscribe(url: string, listener: () => void): () => void {
    let set = this.listeners.get(url);
    if (!set) {
      set = new Set();
      this.listeners.set(url, set);
    }
    set.add(listener);
    return () => {
      set!.delete(listener);
      if (set!.size === 0) this.listeners.delete(url);
    };
  }

  private notify(url: string) {
    this.listeners.get(url)?.forEach((fn) => fn());
  }

  load(url: string, apiKey?: string): void {
    if (this.cache.has(url) || this.statusMap.get(url) === 'loading') return;

    this.statusMap.set(url, 'loading');

    const promise = new Promise<{ blobUrl: string; isBlob: boolean }>((resolve, reject) => {
      this.queue.push({ url, apiKey, resolve, reject });
    });

    promise
      .then(({ blobUrl, isBlob }) => {
        this.evictIfNeeded();
        this.cache.set(url, { blobUrl, isBlob, accessedAt: Date.now() });
        this.statusMap.set(url, 'done');
        this.notify(url);
      })
      .catch(() => {
        this.cache.set(url, { blobUrl: url, isBlob: false, accessedAt: Date.now() });
        this.statusMap.set(url, 'error');
        this.notify(url);
      });

    this.processQueue();
  }

  private async processQueue() {
    while (this.activeCount < MAX_CONCURRENT && this.queue.length > 0) {
      const item = this.queue.shift()!;
      this.activeCount++;
      this.fetchWithRetry(item)
        .then(item.resolve)
        .catch(item.reject)
        .finally(() => {
          this.activeCount--;
          this.processQueue();
        });
    }
  }

  private async fetchWithRetry(
    item: QueueItem,
    attempt = 0,
  ): Promise<{ blobUrl: string; isBlob: boolean }> {
    try {
      const headers: HeadersInit = {};
      if (item.apiKey) {
        headers['Authorization'] = `Bearer ${item.apiKey}`;
      }

      const response = await fetch(item.url, {
        method: 'GET',
        headers,
        mode: 'cors',
        credentials: 'omit',
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      return { blobUrl, isBlob: true };
    } catch (err) {
      if (attempt < MAX_RETRIES - 1) {
        await new Promise((r) => setTimeout(r, RETRY_DELAYS[attempt]));
        return this.fetchWithRetry(item, attempt + 1);
      }
      throw err;
    }
  }

  private evictIfNeeded() {
    if (this.cache.size < MAX_CACHE_SIZE) return;

    const entries = [...this.cache.entries()].sort(
      (a, b) => a[1].accessedAt - b[1].accessedAt,
    );

    const toRemove = entries.slice(0, entries.length - MAX_CACHE_SIZE + 10);
    for (const [key, entry] of toRemove) {
      if (entry.isBlob) {
        URL.revokeObjectURL(entry.blobUrl);
      }
      this.cache.delete(key);
      this.statusMap.delete(key);
    }
  }
}

export const mediaLoader = new MediaLoader();
